import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Grid,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { toast } from 'react-toastify';
import {
  fetchProvisionalBillingSupervisorBanks,
  fetchMyProvisionalBillingRecords,
  submitProvisionalBillingRecords,
  bulkUpdateProvisionalBillingRecords,
} from '../actions/provisionalBillingActions';
import {
  PROVISIONAL_BILLING_SUBMIT_RESET,
  PROVISIONAL_BILLING_BULK_UPDATE_RESET,
} from '../constants/provisionalBillingConstants';

const MONTH_NAMES = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];

const now = new Date();
const CURRENT_MONTH = now.getMonth() + 1;
const CURRENT_YEAR = now.getFullYear();
const YEAR_OPTIONS = Array.from({ length: 5 }, (_, i) => CURRENT_YEAR - 2 + i);

// Merge the current team-member roster (from Supervisor Master) with any values
// already saved against those members, matching by identifier (falling back to name).
const buildMembers = (masterMembers, existingMembers) => {
  const existingMap = new Map((existingMembers || []).map((m) => [m.identifier || m.name, m]));
  return (masterMembers || []).map((m) => {
    const key = m.identifier || m.name;
    const existing = existingMap.get(key);
    return {
      name: m.name,
      identifier: m.identifier || '',
      noOfCases: existing?.noOfCases ?? '',
      collectionAgainstCases: existing?.collectionAgainstCases ?? '',
    };
  });
};

const ProvisionalBilling = () => {
  const dispatch = useDispatch();

  const [month, setMonth] = useState(CURRENT_MONTH);
  const [year, setYear] = useState(CURRENT_YEAR);
  const [sections, setSections] = useState([]);

  const { userLogin: { userInfo } } = useSelector((s) => s);
  const getRoleData = useSelector((s) => s.getRoleInfo?.getRoleData || {});
  const { banks = [], loading: loadingBanks } = useSelector((s) => s.provisionalBillingSupervisorBanks);
  const { records: myRecords = [], loading: loadingRecords } = useSelector((s) => s.provisionalBillingMyRecords);
  const provisionalBillingSubmitState = useSelector((s) => s.provisionalBillingSubmit);
  const provisionalBillingBulkUpdate = useSelector((s) => s.provisionalBillingBulkUpdate);

  const loadData = useCallback(() => {
    dispatch(fetchProvisionalBillingSupervisorBanks(month, year));
    dispatch(fetchMyProvisionalBillingRecords(month, year));
  }, [dispatch, month, year]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Build sections whenever banks or existing records change
  useEffect(() => {
    if (loadingBanks || loadingRecords) return;

    const combos = [];
    for (const b of banks) {
      for (const p of b.products) {
        combos.push({ bank: b.bankName, product: p.productName, members: p.members });
      }
    }

    // Also include combos from existing records not in current master
    for (const rec of myRecords) {
      const exists = combos.find((c) => c.bank === rec.bank && c.product === rec.product);
      if (!exists) combos.push({ bank: rec.bank, product: rec.product, members: rec.members || [] });
    }

    if (combos.length === 0 && myRecords.length === 0) {
      setSections([]);
      return;
    }

    const built = combos.map(({ bank, product, members }) => {
      const existing = myRecords.find((r) => r.bank === bank && r.product === product);
      if (existing) {
        return {
          bank,
          product,
          _id: existing._id,
          provisionalBilling: existing.provisionalBilling ?? '',
          totalCollection: existing.totalCollection ?? '',
          members: buildMembers(members, existing.members),
          isEditing: false,
        };
      }
      return {
        bank,
        product,
        _id: null,
        provisionalBilling: '',
        totalCollection: '',
        members: buildMembers(members, []),
        isEditing: false,
      };
    });

    setSections(built);
  }, [banks, myRecords, loadingBanks, loadingRecords]);

  // Submit success
  useEffect(() => {
    if (provisionalBillingSubmitState.success) {
      toast.success('Provisional billing submitted successfully');
      dispatch({ type: PROVISIONAL_BILLING_SUBMIT_RESET });
      loadData();
    }
    if (provisionalBillingSubmitState.error) {
      toast.error(provisionalBillingSubmitState.error);
      dispatch({ type: PROVISIONAL_BILLING_SUBMIT_RESET });
    }
  }, [provisionalBillingSubmitState.success, provisionalBillingSubmitState.error]);

  // Bulk update success
  useEffect(() => {
    if (provisionalBillingBulkUpdate.success) {
      toast.success('Provisional billing updated successfully');
      dispatch({ type: PROVISIONAL_BILLING_BULK_UPDATE_RESET });
      loadData();
    }
    if (provisionalBillingBulkUpdate.error) {
      toast.error(provisionalBillingBulkUpdate.error);
      dispatch({ type: PROVISIONAL_BILLING_BULK_UPDATE_RESET });
    }
  }, [provisionalBillingBulkUpdate.success, provisionalBillingBulkUpdate.error]);

  const updateSection = (idx, field, value) => {
    setSections((prev) => prev.map((s, i) => (i === idx ? { ...s, [field]: value } : s)));
  };

  const updateMember = (sectionIdx, memberIdx, field, value) => {
    setSections((prev) =>
      prev.map((s, i) => {
        if (i !== sectionIdx) return s;
        return {
          ...s,
          members: s.members.map((m, j) => (j === memberIdx ? { ...m, [field]: value } : m)),
        };
      }),
    );
  };

  const setSectionEditing = (idx, editing) => {
    setSections((prev) => prev.map((s, i) => (i === idx ? { ...s, isEditing: editing } : s)));
  };

  const buildPayloadSection = (s) => ({
    _id: s._id,
    bank: s.bank,
    product: s.product,
    provisionalBilling: s.provisionalBilling,
    totalCollection: s.totalCollection,
    members: s.members.map((m) => ({
      name: m.name,
      identifier: m.identifier,
      noOfCases: m.noOfCases,
      collectionAgainstCases: m.collectionAgainstCases,
    })),
  });

  const handleSubmit = (idx) => {
    const section = sections[idx];
    dispatch(submitProvisionalBillingRecords({ month, year, sections: [buildPayloadSection(section)] }));
  };

  const handleUpdate = (idx) => {
    const section = sections[idx];
    dispatch(bulkUpdateProvisionalBillingRecords([buildPayloadSection(section)]));
  };

  const isSaving = provisionalBillingSubmitState.loading || provisionalBillingBulkUpdate.loading;
  const isLoading = loadingBanks || loadingRecords;
  const canSubmit = getRoleData.provisionalBillingSubmit === true;
  const canUpdate = getRoleData.provisionalBillingUpdate === true;

  return (
    <Box>
      {/* Page Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Provisional Billing
        </Typography>

        {/* Month / Year selectors */}
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={6} sm={3} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Month</InputLabel>
              <Select value={month} label="Month" onChange={(e) => setMonth(e.target.value)}>
                {MONTH_NAMES.map((m, i) => (
                  <MenuItem key={i + 1} value={i + 1}>{m}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Year</InputLabel>
              <Select value={year} label="Year" onChange={(e) => setYear(e.target.value)}>
                {YEAR_OPTIONS.map((y) => (
                  <MenuItem key={y} value={y}>{y}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Box>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : sections.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No bank/product assignments found for {MONTH_NAMES[month - 1]} {year}.
            Please ensure Supervisor Master has entries for this month.
          </Typography>
        </Paper>
      ) : (
        <Paper sx={{ p: 3 }}>
          {/* Sub-heading: Supervisor · Month Year */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" fontWeight="bold" color="primary">
              {userInfo?.name} &nbsp;·&nbsp; {MONTH_NAMES[month - 1]} {year}
            </Typography>
          </Box>

          {/* One section per bank+product */}
          {sections.map((section, idx) => {
            const isSubmitted = section._id != null;
            const isReadOnly = isSubmitted && !section.isEditing;
            return (
            <Box key={`${section.bank}-${section.product}`} sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <Divider sx={{ flex: 1 }} />
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  sx={{ px: 1, whiteSpace: 'nowrap', color: 'text.secondary' }}
                >
                  {section.bank} &nbsp;—&nbsp; {section.product}
                </Typography>
                {isSubmitted && (
                  <Chip
                    label={section.isEditing ? 'Editing' : 'Submitted'}
                    color={section.isEditing ? 'warning' : 'success'}
                    size="small"
                  />
                )}
                <Divider sx={{ flex: 1 }} />
              </Box>

              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth size="small"
                    label="Provisional Billing"
                    type="number"
                    value={section.provisionalBilling}
                    onChange={(e) => updateSection(idx, 'provisionalBilling', e.target.value)}
                    InputProps={{ readOnly: isReadOnly }}
                    disabled={isReadOnly}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth size="small"
                    label="Total Collection"
                    type="number"
                    value={section.totalCollection}
                    onChange={(e) => updateSection(idx, 'totalCollection', e.target.value)}
                    InputProps={{ readOnly: isReadOnly }}
                    disabled={isReadOnly}
                  />
                </Grid>
              </Grid>

              {section.members.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No team members assigned to this bank/product.
                </Typography>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                        <TableCell><strong>Team Member Name</strong></TableCell>
                        <TableCell><strong>No. of Cases</strong></TableCell>
                        <TableCell><strong>Against No. of Cases — Total Collection</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {section.members.map((member, mi) => (
                        <TableRow key={member.identifier || member.name || mi}>
                          <TableCell>{member.name}</TableCell>
                          <TableCell sx={{ width: 200 }}>
                            <TextField
                              fullWidth
                              size="small"
                              type="number"
                              value={member.noOfCases}
                              onChange={(e) => updateMember(idx, mi, 'noOfCases', e.target.value)}
                              InputProps={{ readOnly: isReadOnly }}
                              disabled={isReadOnly}
                            />
                          </TableCell>
                          <TableCell sx={{ width: 260 }}>
                            <TextField
                              fullWidth
                              size="small"
                              type="number"
                              value={member.collectionAgainstCases}
                              onChange={(e) => updateMember(idx, mi, 'collectionAgainstCases', e.target.value)}
                              InputProps={{ readOnly: isReadOnly }}
                              disabled={isReadOnly}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {/* Per-section action button */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                {!isSubmitted && canSubmit && (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => handleSubmit(idx)}
                    disabled={isSaving}
                    startIcon={isSaving ? <CircularProgress size={16} /> : null}
                  >
                    {isSaving ? 'Submitting...' : 'Submit'}
                  </Button>
                )}
                {isSubmitted && !section.isEditing && canUpdate && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setSectionEditing(idx, true)}
                  >
                    Edit
                  </Button>
                )}
                {isSubmitted && section.isEditing && (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => { setSectionEditing(idx, false); loadData(); }}
                      disabled={isSaving}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => handleUpdate(idx)}
                      disabled={isSaving}
                      startIcon={isSaving ? <CircularProgress size={16} /> : null}
                    >
                      {isSaving ? 'Updating...' : 'Update'}
                    </Button>
                  </Box>
                )}
              </Box>
            </Box>
            );
          })}
        </Paper>
      )}
    </Box>
  );
};

export default ProvisionalBilling;
